/* eslint-disable @typescript-eslint/camelcase */
import { SUBJECTS } from '@lib/constants/billing'

export const GA_TRACKING_ID = 'G-RK90KV5REZ'
export const AW_CONVERSION_ID = 'AW-595563012'

// Google Ads Conversion Labels
export const GoogleAdsConversions = {
  // Trial class booked (subject-specific)
  TRIAL_CLASS_BOOKED_CODING: `${AW_CONVERSION_ID}/oHtACMLkoPMCEISk_psC`,
  TRIAL_CLASS_BOOKED_MATH: `${AW_CONVERSION_ID}/fPLdCNPm_-QcEISk_psC`,
  TRIAL_CLASS_BOOKED_ELA: `${AW_CONVERSION_ID}/xTSgCMvM_-QcEISk_psC`,
  // Trial class joined (subject-specific)
  TRIAL_CLASS_JOINED_CODING: `${AW_CONVERSION_ID}/oQx3CJW7180bEISk_psC`,
  TRIAL_CLASS_JOINED_MATH: `${AW_CONVERSION_ID}/sVTKCMTg9eQcEISk_psC`,
  TRIAL_CLASS_JOINED_ELA: `${AW_CONVERSION_ID}/BMW5COv-_-QcEISk_psC`,
  // Trial class completed (subject-specific)
  TRIAL_CLASS_COMPLETED_CODING: `${AW_CONVERSION_ID}/9F-4CJvhyfMCEISk_psC`,
  TRIAL_CLASS_COMPLETED_MATH: `${AW_CONVERSION_ID}/QKfXCPftgOUcEISk_psC`,
  TRIAL_CLASS_COMPLETED_ELA: `${AW_CONVERSION_ID}/M_f9CJysg-UcEISk_psC`,
  // Course purchased (subject-specific)
  COURSE_PURCHASED_CODING: `${AW_CONVERSION_ID}/5s__CI7gy_UCEISk_psC`,
  COURSE_PURCHASED_MATH: `${AW_CONVERSION_ID}/YqJ1CP-BgeUcEISk_psC`,
  COURSE_PURCHASED_ELA: `${AW_CONVERSION_ID}/6C9lCMfIg-UcEISk_psC`,
  // Unchanged (subject-agnostic)
  STUDENT_REGISTERED: `${AW_CONVERSION_ID}/NgXuCNPP-M8bEISk_psC`,
}

// Map each trial-class conversion family to its subject-specific Google Ads labels.
const TRIAL_CONVERSION_LABELS = {
  BOOKED: {
    [SUBJECTS.CODING]: GoogleAdsConversions.TRIAL_CLASS_BOOKED_CODING,
    [SUBJECTS.MATH]: GoogleAdsConversions.TRIAL_CLASS_BOOKED_MATH,
    [SUBJECTS.ELA]: GoogleAdsConversions.TRIAL_CLASS_BOOKED_ELA,
  },
  JOINED: {
    [SUBJECTS.CODING]: GoogleAdsConversions.TRIAL_CLASS_JOINED_CODING,
    [SUBJECTS.MATH]: GoogleAdsConversions.TRIAL_CLASS_JOINED_MATH,
    [SUBJECTS.ELA]: GoogleAdsConversions.TRIAL_CLASS_JOINED_ELA,
  },
  COMPLETED: {
    [SUBJECTS.CODING]: GoogleAdsConversions.TRIAL_CLASS_COMPLETED_CODING,
    [SUBJECTS.MATH]: GoogleAdsConversions.TRIAL_CLASS_COMPLETED_MATH,
    [SUBJECTS.ELA]: GoogleAdsConversions.TRIAL_CLASS_COMPLETED_ELA,
  },
} as const

export type TrialConversionFamily = keyof typeof TRIAL_CONVERSION_LABELS

/**
 * Route a trial-class conversion to its subject-specific Google Ads label.
 * Math and ELA have dedicated labels; Coding and any unknown/empty subject fall
 * back to the Coding (generic) label - mirroring the backend subject_event_name_map
 * in api/apps/analytics/utils.py.
 */
export const getTrialConversionLabel = (
  family: TrialConversionFamily,
  subject?: string | null
): string => {
  const labels = TRIAL_CONVERSION_LABELS[family]
  if (subject === SUBJECTS.MATH) return labels[SUBJECTS.MATH]
  if (subject === SUBJECTS.ELA) return labels[SUBJECTS.ELA]
  return labels[SUBJECTS.CODING]
}

const COURSE_PURCHASE_CONVERSION_LABELS = {
  [SUBJECTS.CODING]: GoogleAdsConversions.COURSE_PURCHASED_CODING,
  [SUBJECTS.MATH]: GoogleAdsConversions.COURSE_PURCHASED_MATH,
  [SUBJECTS.ELA]: GoogleAdsConversions.COURSE_PURCHASED_ELA,
} as const

/**
 * Route a course-purchase conversion to its subject-specific Google Ads label.
 * Math and ELA have dedicated labels; Coding and any unknown/empty subject fall
 * back to the Coding (generic) label - mirroring the backend
 * subject_event_name_map in api/apps/analytics/utils.py.
 */
export const getCoursePurchaseConversionLabel = (
  subject?: string | null
): string =>
  (subject && COURSE_PURCHASE_CONVERSION_LABELS[subject]) ||
  COURSE_PURCHASE_CONVERSION_LABELS[SUBJECTS.CODING]

declare global {
  interface Window {
    gtag: any
  }
}

// https://developers.google.com/analytics/devguides/collection/gtagjs/pages
export const pageview = (url: string) => {
  window?.gtag('config', GA_TRACKING_ID, {
    page_path: url,
    allow_enhanced_conversions: true,
  })

  // Set event_source parameter for page_view events
  window?.gtag('set', {
    event_source: 'web',
  })
}

// https://developers.google.com/analytics/devguides/collection/gtagjs/events
type EventProps = {
  action: string
  id: string
}

export const recordEvent = ({ action, id }: EventProps) => {
  if (window.gtag)
    window.gtag('event', action, {
      ['send_to']: `${AW_CONVERSION_ID}/${id}`,
    })
}

/**
 * Set user_id for GA4 tracking.
 * This allows tracking the same user across sessions and devices.
 *
 * @param userId - Internal user ID (not PII)
 */
export const setGA4UserId = (userId: number | string) => {
  if (!window?.gtag || !userId) return

  window.gtag('config', GA_TRACKING_ID, {
    user_id: userId.toString(),
  })
}

/**
 * User properties for GA4 tracking.
 * These properties are attached to all subsequent events.
 */
type GA4UserProperties = {
  // Core properties
  user_type?: 'student' | 'teacher' | 'guest'
  grade?: string
  country?: string
  city?: string
  timezone?: string
  gender?: string

  // User status
  is_paid_user?: boolean
  is_vip?: boolean
  is_competition_user?: boolean
  has_laptop?: boolean

  // Acquisition
  signup_source?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string

  // Student-specific
  enrolled_subjects?: string
  has_completed_course_class?: boolean
  is_trial_class_scheduled?: boolean
  has_coding_teacher?: boolean
  has_math_teacher?: boolean
  has_ela_teacher?: boolean

  // Teacher-specific
  is_verified_teacher?: boolean
  teacher_has_completed_class?: boolean
  educational_qualification?: string
}

/**
 * Set custom user properties for GA4.
 * Properties are attached to the user and included in all events.
 * Only include non-PII data.
 *
 * @param properties - Custom user properties
 */
export const setGA4UserProperties = (properties: GA4UserProperties) => {
  if (!window?.gtag) return

  const cleanedProperties: Record<string, { value: string | boolean }> = {}

  // Helper to add property if it exists
  const addProp = (key: string, value: string | boolean | undefined) => {
    if (value !== undefined && value !== null && value !== '') {
      cleanedProperties[key] = { value }
    }
  }

  // Core properties
  addProp('user_type', properties.user_type)
  addProp('grade', properties.grade)
  addProp('country', properties.country)
  addProp('city', properties.city)
  addProp('timezone', properties.timezone)
  addProp('gender', properties.gender)

  // User status
  addProp('is_paid_user', properties.is_paid_user)
  addProp('is_vip', properties.is_vip)
  addProp('is_competition_user', properties.is_competition_user)
  addProp('has_laptop', properties.has_laptop)

  // Acquisition
  addProp('signup_source', properties.signup_source)
  addProp('utm_source', properties.utm_source)
  addProp('utm_medium', properties.utm_medium)
  addProp('utm_campaign', properties.utm_campaign)

  // Student-specific
  addProp('enrolled_subjects', properties.enrolled_subjects)
  addProp('has_completed_course_class', properties.has_completed_course_class)
  addProp('is_trial_class_scheduled', properties.is_trial_class_scheduled)
  addProp('has_coding_teacher', properties.has_coding_teacher)
  addProp('has_math_teacher', properties.has_math_teacher)
  addProp('has_ela_teacher', properties.has_ela_teacher)

  // Teacher-specific
  addProp('is_verified_teacher', properties.is_verified_teacher)
  addProp('teacher_has_completed_class', properties.teacher_has_completed_class)
  addProp('educational_qualification', properties.educational_qualification)

  // Only call gtag if we have properties to set
  if (Object.keys(cleanedProperties).length > 0) {
    window.gtag('set', 'user_properties', cleanedProperties)
  }
}
